// ============================================================
// MIDAS Chat Bot -- TypeScript Models
// ============================================================

/** Role of a message sender */
export type MessageRole = "user" | "assistant";

/** A source document referenced by a bot response */
export interface ChatSource {
  document: string;
  page?: number;
  pageFrom?: number;
  pageTo?: number;
  url?: string;
  imageUrl?: string;
  /** R5 sub-task 1: secondary lookup key when bot omits `page`.
   *  Bot may emit a sheet/document name (e.g. "Vstupný modul 1") alongside
   *  imageUrl when no discrete page integer is available. The artifact-side
   *  resolver normalises this (lowercased + trimmed) and matches against
   *  artifact.title / data.name / data.page_name. Optional. */
  name?: string;
}

/** A service report cited by the bot (MC-22) */
export interface SrCitation {
  /** The SR number in canonical form, e.g. "SR-260326-002" */
  reportNumber: string;
  /** How many chunks in the retrieved context referenced this SR (>=1) */
  citationCount: number;
}

/** A single message in the conversation */
export interface ChatMessage {
  id: string;
  /** R5 sub-task 5a: bot-minted message_id from the SSE `answer` (or `done`)
   *  event's `answer_data.message_id`. Stable identifier for the assistant
   *  reply — used by Like/Dislike feedback POSTs to `/v1/bot/midas/feedback`.
   *  Optional because older bot versions do not emit it; the frontend gates
   *  the POST behind its presence and logs a one-time warning when absent. */
  botMessageId?: string;
  role: MessageRole;
  text: string;
  timestamp: string;
  sources?: ChatSource[];
  suggestions?: string[];
  /** True if the message failed to send (user messages only) */
  sendFailed?: boolean;
  /** Bot response intent — PLATFORM_* prefix means platform mode, no prefix means machine mode */
  intent?: string;
  /** Artifacts attached by the bot (PDF page images, platform files, etc.) — M2+ */
  artifacts?: ChatArtifact[];
  /** Blob URL of an image attached by the user (M7-A: image upload) */
  imageUrl?: string;
  /** Original filename of the user-attached image */
  imageName?: string;
  /** Action buttons attached by the bot (M7-D: action buttons) */
  actions?: ChatAction[];
  /** ID of the action that was clicked (once set, the action group is read-only) */
  clickedActionId?: string;
  /** Action request data for the MIDAS Action Framework (MA-0) */
  actionRequest?: ChatActionRequest;
  /** Tier-0 action requests rendered as chips (nav.switch_machine, nav.switch_platform, etc.) */
  uiActionRequests?: ChatActionRequest[];
  /** Service report citations — NEW (MC-22). Each entry renders as a clickable chip. */
  srCitations?: SrCitation[];
}

/** Action request embedded in a chat message (MA-0: Action Framework) */
export interface ChatActionRequest {
  action_id: string;
  action_type: string;
  schema: {
    fields: ChatActionSchemaField[];
  };
  values: Record<string, any>;
  display: {
    title: string;
    summary?: string;
    icon?: string;
  };
  approval_tier: number;
  /** Localized display label from bot (e.g. machine name for nav, "Create Service Report" for SR) */
  label?: string;
  /** Optional rendering hint from bot: "chip", "form_card", "modal". Supplements approval_tier. */
  render_hint?: string;
}

/** Schema field definition within an action request */
export interface ChatActionSchemaField {
  name: string;
  type: "text" | "textarea" | "dropdown" | "file" | "date" | "number" | "currency" | "readonly";
  label: string;
  description?: string;
  required?: boolean;
  max_length?: number;
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
  accept?: string[];
  max_size?: number;
  display_hints?: {
    emphasis?: "normal" | "high" | "critical";
    kind?: string;
    group?: string;
    help_text?: string;
    dependency?: { field: string; value: string };
  };
}

/** Supported artifact types */
export type ChatArtifactType =
  | "image"
  | "platform_file"
  | "table"
  | "machine_card"
  | "sr_summary"
  | "diagram_overlay"
  /** MIDAS-BUG-001-P2: user-uploaded image attached to a chat message */
  | "user_image";

/** Data payload for type: "user_image" artifacts (MIDAS-BUG-001-P2) */
export interface UserImageArtifactData {
  mime: string;
  size: number;
  filename: string;
  width?: number;
  height?: number;
}

/** An artifact attached to a bot message (parsed from SSE answer event).
 *  MC-25: `source` may also be "cross_reference" for list-ref artifacts. */
export interface ChatArtifact {
  type: ChatArtifactType;
  title: string;
  /** "bot" and "machineid" are legacy; "user" = user-uploaded image (P2);
   *  "cross_reference" = MC-25 cross-reference artifact paired with <list-ref> chips. */
  source: "bot" | "machineid" | "user" | "cross_reference";
  url: string;
  document_id?: string;
  page?: number;
  mime_type?: string;
  entity_type?: string;
  entity_id?: string;
  file_id?: string;
  /** Structured data payload for rich artifacts (table, machine_card, sr_summary, user_image) */
  data?:
    | TableArtifactData
    | MachineCardArtifactData
    | SRSummaryArtifactData
    | DiagramOverlayArtifactData
    | UserImageArtifactData;
}

// ============================================================
// Rich Artifact Data Shapes (M3)
// ============================================================

/** Column definition for a table artifact */
export interface TableArtifactColumn {
  key: string;
  label: string;
  width?: string;
}

/** A single row in a table artifact */
export interface TableArtifactRow {
  [key: string]: string | number | null | undefined;
  link?: string;
}

/** Data payload for type: "table" artifacts */
export interface TableArtifactData {
  columns: TableArtifactColumn[];
  rows: TableArtifactRow[];
  total_count?: number;
}

/** Data payload for type: "machine_card" artifacts */
export interface MachineCardArtifactData {
  machine_id: string;
  name: string;
  manufacturer: string;
  model: string;
  serial_number?: string;
  internal_number?: string;
  production_year?: number;
  type?: string;
  control_system?: string;
  image_url?: string;
  open_service_reports?: number;
  last_service_date?: string;
  link: string;
}

/** Timeline event in an SR summary artifact */
export interface SRTimelineEvent {
  event: string;
  timestamp: string;
  user?: string;
}

/** Data payload for type: "sr_summary" artifacts */
export interface SRSummaryArtifactData {
  id: string;
  number: string;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  machine_model?: string;
  created_at: string;
  offers_count?: number;
  assigned_provider?: string;
  timeline?: SRTimelineEvent[];
  link: string;
}

/** A single hotspot on a diagram page */
export interface DiagramHotspot {
  vi_id: string;
  label: string;
  description?: string;
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  type?: string;
  component_id?: string;
  confidence?: number;
  position_source?: string;
}

/** Wiring connection between two components on a diagram page */
export interface DiagramWiringConnection {
  from_component: string;
  from_terminal: string;
  to_component: string;
  to_terminal: string;
  wire_label: string;
}

/** Cross-reference from a component to another page/sheet */
export interface DiagramCrossReference {
  from_component: string;
  target_page: number;
  target_sheet: string;
  reference_text: string;
}

/** Wire label entry on a diagram page */
export interface DiagramWireLabel {
  label: string;
  from_component: string;
  to_component: string;
  cross_section: string;
  color: string;
}

/** Response from GET /v1/bot/diagram/{page}/hotspots */
export interface DiagramHotspotsResponse {
  page_number: number;
  page_id?: string;
  page_title?: string;
  image_url: string;
  hotspots: DiagramHotspot[];
  count: number;
  wiring_connections?: DiagramWiringConnection[];
  cross_references?: DiagramCrossReference[];
  wire_labels?: DiagramWireLabel[];
}

/** A single page entry from the page-list endpoint (Phase 2) */
export interface DiagramPage {
  page_number: number;
  page_id: string;
  page_title: string;
  image_url: string;
  component_count: number;
}

/** Response from GET /v1/bot/diagram/pages (Phase 2) */
export interface DiagramPageListResponse {
  pages: DiagramPage[];
  total_count: number;
}

/** A single search result from the diagram search endpoint (Phase 2) */
export interface DiagramSearchResult {
  component_id: string;
  label: string;
  type: string;
  page_number: number;
  page_title: string;
  confidence: number;
}

/** Response from GET /v1/bot/diagram/search (Phase 2) */
export interface DiagramSearchResponse {
  results: DiagramSearchResult[];
  query: string;
  total_count: number;
}

/** Response from GET /v1/bot/diagram/component/{id} (Phase 2) */
export interface DiagramComponentDetailResponse {
  component_id: string;
  label: string;
  type: string;
  description?: string;
  pages: number[];
  wiring_connections: DiagramWiringConnection[];
  cross_references: DiagramCrossReference[];
}

/** Data payload for type: "diagram_overlay" artifacts.
 *  MC-25/MC-26: extended with optional list metadata + localised info_prompt.
 *  All new fields are optional; artifacts without them degrade gracefully. */
export interface DiagramOverlayArtifactData {
  page: number;
  components: string[];
  image_url: string;
  hotspots_url: string;
  /** MC-25: supplementary display/debug ID (e.g. "050"). NOT the join key — `page` is. */
  list_id?: string;
  /** MC-27: Qdrant collection ID the artifact was retrieved from.
   *  Passed back to the bot in `page_info_request.collection` on (i) clicks
   *  so the bot can skip intent classification + vector search.
   *  Valid values: sbl500_manual_toc_v2 | sbl500_electrical_toc_v2 | SINUMERIK_toc_v2.
   *  If absent on older artifacts, callers fall back to sbl500_electrical_toc_v2
   *  (all electrical schema artifacts come from that collection). */
  collection?: string;
  /** Legacy / generic name field. */
  page_name?: string;
  /** Locale-specific page name (default / Slovak) used by the (i) friendly-text constructor. */
  name?: string;
  /** English page name. */
  name_en?: string;
  /** Czech page name. */
  name_cs?: string;
  /** MC-26: localised info prompt sent as the stream body when the user clicks the (i) button.
   *  NOT displayed in the chat bubble — a friendly short message is constructed instead. */
  info_prompt?: {
    sk?: string;
    en?: string;
    cs?: string;
  };
}

/** An action button attached to a bot message */
export interface ChatAction {
  id: string;
  label: string;
  style: "primary" | "secondary" | "text";
  context?: Record<string, unknown>;
}

/** Machine context set via "Chat about this machine" button */
export interface MachineContext {
  machineId: string;
  model: string;
  serialNumber: string;
  // Display fields (optional — may not always be available from API)
  internalNumber?: string;
  system?: string;
  manufacturerName?: string;
  typeName?: string;
  axisConfigName?: string;
  controlSystemBrandName?: string;
  imageContent?: string; // base64 image content
  description?: string;
  productionYear?: number;
}

// ============================================================
// API Request / Response Types
// ============================================================

/** Result of an executed action, posted back to bot for conversation continuity (MA-0b.3) */
export interface ActionResultPayload {
  action_id: string;
  action_type: string;
  success: boolean;
  result?: Record<string, any>;
  reason?: string;
}

/** POST /v1/bot/chat -- request body */
export interface SendMessageRequest {
  message: string;
  machine_id?: string;
  language?: string;
  /** MA-0b.3: Action result posted back to bot after frontend execution */
  action_result?: ActionResultPayload;
}

/** POST /v1/bot/chat -- response body */
export interface SendMessageResponse {
  message: string;
  sources?: ChatSource[];
  suggestions?: string[];
  /** Service report citations — NEW (MC-22). Optional; absent on older bot versions. */
  srCitations?: SrCitation[];
  metadata?: {
    processingTime?: number;
    model?: string;
    intent?: string;
  };
}

/** GET /v1/bot/chat/history -- response body (snake_case from bot) */
export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
  session_start?: string;
  machine_context?: {
    machine_id: string;
    model: string;
    serial_number: string;
  } | null;
}

/** A single message from the history endpoint */
export interface ChatHistoryMessage {
  role: MessageRole;
  text: string;
  timestamp: string;
  sources?: ChatSource[];
}

/** POST /v1/bot/chat/context -- request body (snake_case for Python bot gateway) */
export interface SetContextRequest {
  machine_id: string | null;
}

/** POST /v1/bot/chat/context -- response body (machine mode entry, snake_case from bot) */
export interface SetContextResponse {
  machine: {
    machine_id: string;
    model: string;
    serial_number: string;
  };
  confirmation_message: string;
}

/** POST /v1/bot/chat/context -- response body (machine mode exit, machineId=null) */
export interface ClearContextResponse {
  mode: string;
  message: string;
}

/** GET /v1/customer/settings/bot-chat -- response body */
export interface BotAccessResponse {
  enabled: boolean;
  globalEnabled: boolean;
}

/** GET /v1/bot/health -- response body */
export interface HealthCheckResponse {
  status: string;
  botVersion?: string;
}

/** SSE event types received during streaming */
export type SSEEventType = "status" | "answer" | "token" | "thread_created" | "action_request" | "done" | "error";

/** SSE event payload */
export interface SSEEvent {
  event: SSEEventType;
  data: string;
}

// ============================================================
// Thread-Scoped Streaming (MIDAS-SCOPESTREAM)
// ============================================================

/** Sentinel thread ID used by the popup chatbot (no real threads). */
export const POPUP_THREAD_ID = "popup";

/** Per-thread streaming buffer. Stores only streaming-related fields
 *  plus a single pendingBotMessage for background-completing streams.
 *  Messages continue to live in state.messages (the global array). */
export interface ThreadBuffer {
  /** The live streaming text being built up token by token. */
  streamingText: string | null;
  /** Whether this thread's stream is in flight. */
  isLoading: boolean;
  /** Progress step text ("Searching documents...", "Analyzing..."). */
  currentStep: string | null;
  /** Error message if the stream failed. */
  error: string | null;
  /** When a stream completes while the user is on a DIFFERENT thread,
   *  the final bot message is stored here. On next loadThread for this
   *  thread, it gets appended to the API-fetched messages. */
  pendingBotMessage: ChatMessage | null;
  /** Timestamp of last activity (for GC eviction ordering). */
  lastActivity: number;
}

// ============================================================
// Redux State Shape
// ============================================================

/** Action state tracked per action_id (MA-0: Action Framework) */
export interface ChatActionStatus {
  actionId: string;
  actionType: string;
  state: "pending" | "executing" | "success" | "error";
  result?: {
    success: boolean;
    entityId?: string;
    entityNumber?: string;
    message: string;
    link?: string;
    data?: Record<string, any>;
  };
  error?: string;
  createdAt: number;
}

export interface ChatBotState {
  /** Whether the chat panel overlay is visible */
  isOpen: boolean;
  /** Full conversation messages */
  messages: ChatMessage[];
  /** Waiting for bot response */
  isLoading: boolean;
  /** Current SSE progress step text (i18n key) */
  currentStep: string | null;
  /** Currently selected machine context (or null) */
  machineContext: MachineContext | null;
  /** Whether machine context API call is in progress */
  machineContextLoading: boolean;
  /** Last health check result */
  isBotAvailable: boolean;
  /** Feature flag result (per-company + global) */
  isBotEnabled: boolean;
  /** Whether the checkBotAccess call has completed (true after fulfilled OR rejected) */
  botAccessChecked: boolean;
  /** Bot responded while panel was closed */
  hasUnread: boolean;
  /** Last error message */
  error: string | null;
  /** Whether the inline machine picker is shown (replaces Dialog) */
  showMachinePicker: boolean;
  /** Accumulated token text during SSE streaming (progressive rendering) */
  streamingText: string | null;

  /** Platform mode messages saved when entering machine mode */
  savedPlatformMessages: ChatMessage[];
  /** Machine mode messages saved per machine (keyed by machineId) */
  savedMachineMessages: Record<string, ChatMessage[]>;

  /** Action states tracked by action_id (MA-0: Action Framework) */
  pendingActions: Record<string, ChatActionStatus>;
  /** Set of executed action_ids to prevent duplicate execution */
  executedActionIds: string[];

  /** When true, next message will include new_thread: true in the request body.
   * Set by startNewChat, consumed (cleared) by sendMessage. */
  newThreadPending: boolean;

  /** Per-thread streaming buffers. Keyed by threadId (or temp optimistic ID for new threads).
   *  Does NOT store messages -- only streaming state + pendingBotMessage for background completions. */
  threadBuffers: Record<string, ThreadBuffer>;

  /** Maps each in-flight stream to its originating thread ID.
   *  Key = thunk requestId (from meta.requestId), Value = threadId.
   *  Supports multiple concurrent streams. */
  activeStreams: Record<string, string>;
}
